/* MUI */
import { Alert, Box } from '@mui/material';

/* Hooks */
import { useFetchTenant } from 'hooks/dataAccess/Tenant/tenant';

/* React */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/* Internal Components */
import {
    INTEGRATION_OPTION_GOOGLE_AUTOMATED,
    INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED
} from './Components/IntegrationSelector';
import { ChooseIntegration } from './Steps/ChooseIntegration';
import { ConfigureIntegration } from './Steps/ConfigureIntegration';

export const SetupIntegration = ({
    formFooterComponent,
    filterExistingSyncedTenants,
    integrationAuthenticated,
    integrationType,
    isGoogleEnabled,
    isMicrosoftEnabled,
    isSetUp,
    handleProviderSelection,
    setIntegrationType,
    tenantSyncConfig,
    onSuccess
}) => {
    const { clientId } = useParams();
    const { t } = useTranslation();
    const providerFormRef = useRef(null);
    const { tenant } = useFetchTenant({ clientId });

    useEffect(() => {
        // We land here when client previously had an integration that then forfeited.
        // And now want to re-integrate. Since they can't swtich between Google and Microsoft
        // we keep them "enabled".
        if (isGoogleEnabled) {
            setIntegrationType(INTEGRATION_OPTION_GOOGLE_AUTOMATED);
            handleProviderSelection(INTEGRATION_OPTION_GOOGLE_AUTOMATED);
        }
        if (isMicrosoftEnabled) {
            setIntegrationType(INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED);
            handleProviderSelection(
                INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED
            );
        }
    }, [
        handleProviderSelection,
        isGoogleEnabled,
        isMicrosoftEnabled,
        setIntegrationType
    ]);

    const initialStep = isGoogleEnabled || isMicrosoftEnabled ? 2 : 3;

    let providerTranslation = '';
    if (isGoogleEnabled) {
        providerTranslation = 'GoogleConfiguration';
    }

    if (isMicrosoftEnabled) {
        providerTranslation = 'MicrosoftConfiguration';
    }

    return (
        <Box sx={{ backgroundColor: 'background.configureIntegration' }}>
            {isSetUp ? (
                <Box mx="24px">
                    <Alert
                        severity="success"
                        icon={false}
                        style={{
                            marginBottom: '16px',
                            marginTop: '16px'
                        }}
                    >
                        {providerTranslation
                            ? t(`Provider.${providerTranslation}.is_configured`)
                            : t(`Provider.is_configured`)}
                    </Alert>
                </Box>
            ) : (
                <>
                    {!isGoogleEnabled && !isMicrosoftEnabled && (
                        <ChooseIntegration
                            integrationType={integrationType}
                            setIntegrationType={setIntegrationType}
                            useIntegrationWizard={false}
                            handleProviderSelection={handleProviderSelection}
                        />
                    )}
                    <ConfigureIntegration
                        clientId={clientId}
                        formFooterComponent={formFooterComponent}
                        initialStep={initialStep}
                        integrationType={integrationType}
                        integrationAuthenticated={integrationAuthenticated}
                        filterExistingSyncedTenants={
                            filterExistingSyncedTenants
                        }
                        onSuccess={onSuccess}
                        providerFormRef={providerFormRef}
                        setIntegrationType={setIntegrationType}
                        tenantSyncConfig={tenantSyncConfig}
                        tenantToEditIntegration={tenant}
                        useIntegrationWizard={false}
                    />
                </>
            )}
        </Box>
    );
};
